import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ComponentDefinition, Ticket, IDateRangePickerOptions, ApplicationUser, IErrorReporting, TicketViewModel, TicketStatus, TicketType, TicketStatusId, ApplicationUserViewModel } from '../../models/index';
import { ICurrentApplicationService, IAuthenticationService, IDateRangeOptionsBuilder, ILoggerService, IStoreService, IapplicationUserService, ITranslatedLoggerService, ITicketAttachmentService } from '../../services';
import { StoreElement } from '../../stateManagement';
import { Roles, guidEmpty } from '../../common/constants';
import { TicketAccessRights } from './TicketAccessRights';
import { getClasses } from '../ticketModalDetails/getClasses';

class TicketModalController implements angular.IComponentController, angular.IOnDestroy {
    allowedStatuses: TicketStatus[];
    accessRights: TicketAccessRights;
    categories: TicketType[];
    currentTicketModalPage = 0;
    dueRange: { date: string };
    dueDateOptions: IDateRangePickerOptions;
    editedTicketViewModel?: TicketViewModel;
    filesToUpload: File[] = [];
    formController: angular.IFormController;
    isOpen: boolean;
    modalId: string;
    onSaved: () => void;

    // parentId: number;
    parent: TicketViewModel;

    revisedDueRange: { date: string };
    revisedDueDateOptions: IDateRangePickerOptions;
    statuses: TicketStatus[];
    ticket: Ticket;
    ticketUploadMaxSize: number;
    ticketUploadAllowedFileTypes: string[];
    users: ApplicationUserViewModel[];
    validationMessages: IErrorReporting[];

    private _allowedAssign = [Roles.master, Roles.engineer, Roles.administrator];
    private _allowedUpdateTitleAndDescription = [Roles.master];
    private _currentUser: ApplicationUser;
    private _unsubscribe: Function;

    static $inject = [
        '$scope',
        'storeService',
        'ticketAttachmentService',
        'currentApplicationService',
        'authenticationService',
        'applicationUserService',
        'dateRangeOptionsBuilder',
        'translatedLoggerService',
    ];

    constructor(
        private _$scope: angular.IScope,
        private _storeService: IStoreService,
        private _ticketAttachmentService: ITicketAttachmentService,
        private _currentApplicationService: ICurrentApplicationService,
        private _authenticationService: IAuthenticationService,
        private _applicationUserService: IapplicationUserService,
        private _dateRangeOptionsBuilder: IDateRangeOptionsBuilder,
        private _loggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this.accessRights = this.accessRights || {
            canAssign: false,
            canUpdateTitleAndDescription: true,
            canUpdateCategory: false,
            canCreateCategory: this.userInRole(this._allowedAssign),
            canUpdateDueDate: true,
            canUpdateRevisedDueDate: false
        };
        this.currentTicketModalPage = 0;
        this.initTicket();
        this.initDefault();
        this.initStore();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        // Initialize ticket in case of edition
        if (changesObj['editedTicketViewModel']) {
            this.initTicket();
        }
        // Set parentId in case of action item
        if (changesObj['parent'] && this.ticket && !this.ticket.parentTicketId) {
            this.ticket.parentTicketId = this.parent?.model.id;
        }
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    // Initialize ticket (either with existing ticket if update or empty ticket if add)
    initTicket() {
        this.currentTicketModalPage = 0;
        const isMaster = this.userInRole([Roles.master]);
        if (this.editedTicketViewModel) {
            this.ticket = (Object as any).assign({}, this.editedTicketViewModel.model);
            this.dueRange = this.setDueDateRange(this.ticket.dueDate);
            this.revisedDueRange = this.setRevisedDueDateRange(this.ticket.revisedDueDate, this.ticket.dueDate);
            this.allowedStatuses = this.statuses
                ? this.statuses.filter(s => this.editedTicketViewModel.assignableStatuses.indexOf(s.id) >= 0 || isMaster)
                : [];
            this.updateDueDatesOptions();
        } else {
            this.resetTicket();
            const defaultStatuses = [TicketStatusId.New];
            if (this.userInRole(this._allowedAssign)) {
                defaultStatuses.push(TicketStatusId.Assigned);
            }
            this.allowedStatuses = this.statuses
                ? [].concat(...defaultStatuses.map(d => this.statuses.filter(s => s.id === d)))
                : [];
        }
        if (this.ticket && !this.ticket.parentTicketId && this.parent) {
            this.ticket.parentTicketId = this.parent.model.id;
        }

        // update access right
        const userId = this._authenticationService.authentication.userGuid;
        if (this.ticket) {
            this.setAccessRights(userId, isMaster);
        } else {
            this.accessRights = {
                canAssign: this.canAssign(),
                canCreateCategory: this.userInRole(this._allowedAssign),
                canUpdateTitleAndDescription: false,
                canUpdateCategory: false,
                canUpdateDueDate: true,
                canUpdateRevisedDueDate: false,
            };
        }
    }

    private setAccessRights(userId: string, isMaster: boolean) {
        const { statusId } = this.ticket;
        const isMineOrAsignee = this.ticket.assignedUser === userId || this.ticket.creationUser === userId;
        const isInRoles = this.userInRole(this._allowedUpdateTitleAndDescription);
        const isTicketNew = statusId === TicketStatusId.New;
        const isTicketClosed = statusId === TicketStatusId.Closed;
        const newOrMineOrInRole = isTicketNew || isMineOrAsignee || isInRoles || isMaster;

        const canUpdateDueDate = isTicketNew && (!this.ticket.creationUser || this.ticket.creationUser === userId || isMaster);
        this.accessRights = {
            canAssign: this.canAssign(),
            canCreateCategory: this.userInRole(this._allowedAssign),
            canUpdateTitleAndDescription: (!isTicketClosed && newOrMineOrInRole) || isMaster,
            canUpdateCategory: newOrMineOrInRole || isMaster,
            canUpdateDueDate,
            canUpdateRevisedDueDate: !canUpdateDueDate &&
                statusId !== TicketStatusId.New
                && statusId !== TicketStatusId.Rejected
                && statusId !== TicketStatusId.Completed,
        };
    }

    getClasses(name: string, isDisabled = false) {
        return getClasses(name, this.formController, isDisabled);
    }
    
    // Initialize default values
    initDefault() {
        const config = this._currentApplicationService.getApplicationConfiguration();

        this.currentTicketModalPage = 0;
        this.accessRights.canAssign = this.canAssign();
        this.initUsers(config);

        const options = this._dateRangeOptionsBuilder.translate({
            applyClass: 'btn-primary',
            timePicker: false,
            autoApply: true,
            autoUpdateInput: true,
            timePicker24Hour: true,
            singleDatePicker: true,
            showDropdowns: true,
            locale: {
                format: 'YYYY-MM-DD',
                firstDay: config.weekBeginDay,
                daysOfWeek: moment.weekdaysMin() as [string, string, string, string, string, string, string]
            },
            minDate: moment().startOf('d'),
            opens: 'left',
            drops: 'up'
        });
        this.revisedDueDateOptions = angular.copy(options);
        this.dueDateOptions = options;
    }

    private initUsers(config: { guid: string }) {

        const currentUserName = this._authenticationService.authentication.userName;
        const defaultUser: ApplicationUserViewModel = {
            userId: guidEmpty,
            name: '',
            roles: [],
            displayName: '',
            guid: guidEmpty,
            title: '',
            firstName: '',
            lastName: '',
            company: '',
            initials: '',
            phoneNumber: '',
            email: '',
            isSuperAdmin: undefined
        };

        // Endpoint access is resticted
        this._applicationUserService
            .getUsers(config.guid)
            .then(data => {
                this.users = [defaultUser].concat(data);
                this._currentUser = _.find(this.users, u => u.name === currentUserName);

                if (this._currentUser) {
                    // Can assign a ticket
                    this.accessRights.canAssign = this.canAssign();
                }
            });
    }

    // Initialize Redux
    initStore() {
        this._unsubscribe = this._storeService.register(
            store => {
                const isMaster = this.userInRole([Roles.master]);
                this.statuses = _.values<TicketStatus>(store.ticketStatuses.dictionary);
                this.allowedStatuses = this.editedTicketViewModel
                    ? this.statuses.filter(s => this.editedTicketViewModel.assignableStatuses.indexOf(s.id) >= 0 || isMaster)
                    : [];
                this.categories = _.values<TicketType>(store.ticketTypes.dictionary);
            },
            [StoreElement.TicketStatuses, StoreElement.TicketTypes]
        );
    }


    // Add or update ticket
    onSave(): angular.IPromise<void> {
        this.ticket.dueDate = this.dueRange.date;
        if (this.ticket.id && this.revisedDueRange.date && moment(this.revisedDueRange.date) > moment(this.ticket.dueDate)) {
            this.ticket.revisedDueDate = this.revisedDueRange.date;
        }

        const promise = this.ticket.id
            ? this._storeService.dispatchers().tickets.updateOne(this.ticket)
            : this._storeService.dispatchers().tickets.addOne(this.ticket);

        return promise
            .then(data => this.onAfterSave(data))
            .catch(message => this._loggerService.logError(message))
            .finally(() => this.isOpen = false);
    }

    // Initialize modal on open
    onOpen() {
        const config = this._currentApplicationService.getApplicationConfiguration();
        this.initUsers(config);
        this.initTicket();
        const days = moment.weekdaysMin() as [string, string, string, string, string, string, string];
        this.dueDateOptions.locale.format = 'YYYY-MM-DD';
        this.dueDateOptions.locale.daysOfWeek = days;
        this.isOpen = true;
        this.updateDueDatesOptions();
        this._$scope.$apply();
    }

    // Handle cancel button
    onCancel() {
        this.isOpen = false;
        if (!this.editedTicketViewModel) {
            this.resetTicket();
        } else {
            this.restoreTicket();
        }
        this.formController.$setPristine();
    }

    // Check if ticket can be saved
    canSave() {
        return this.formController.$valid
            && (this.ticket.description || this.editedTicketViewModel)
            && (this.formController.$dirty
                || (this.formController['dueDate'] && this.formController['dueDate'].$touched)
                || (this.formController['revisedDueDate'] && this.formController['revisedDueDate'].$touched));
    }

    // Reset current ticket
    resetTicket() {
        this.currentTicketModalPage = 0;
        this.ticket = {
            statusId: TicketStatusId.New,
            assignedUser: guidEmpty,
            parentTicketId: this.parent?.model.id
        } as any;
        this.filesToUpload = [];
        const dueDate = moment().startOf('d').add(1, 'w').toISOString();
        this.dueRange = {
            date: dueDate
        };
        this.revisedDueRange = {
            date: dueDate
        };
        this.updateDueDatesOptions();
    }

    restoreTicket() {
        if (this.editedTicketViewModel) {
            this.ticket = (Object as any).assign({}, this.editedTicketViewModel.model);
        }
    }

    private onAfterSave(data: Ticket[]) {
        if (data && data.length) {
            if (!this.editedTicketViewModel) {
                this._loggerService.logSuccess('Ticket created successfully');
                this._ticketAttachmentService.uploadAttachments(data[0].id, this.filesToUpload)
                    .then(() => this.resetTicket())
                    .catch(() => this._loggerService.logError('File cannot be uploaded'));
            } else {
                this._loggerService.logSuccess('Ticket updated successfully');
            }
            this.formController.$setPristine();
            if (this.onSaved) {
                this.onSaved();
            }
        } else {
            console.log('no data');
        }
    }

    private canAssign(): boolean {
        const userId = this._authenticationService.authentication.userGuid;
        return this.userInRole(this._allowedAssign) || (this.parent && this.parent.model.assignedUser === userId);
    }

    // Set due date range for date picker
    // API return date without 'Z' at the end, which cause issue with the date picker
    private setDueDateRange(dueDate: any): { date: string } {
        const dueRange = {
            date: !dueDate
                ? undefined
                : dueDate.substr(dueDate.length - 1) === 'Z'
                    ? dueDate
                    : dueDate + 'Z'
        };
        return dueRange;
    }

    // Set revised due date range for date picker
    // API return date without 'Z' at the end, which cause issue with the date picker
    private setRevisedDueDateRange(revisedDueDate: any, dueDate: any): { date: string } {
        const dueDateRange = this.setDueDateRange(dueDate);
        const revisedDueRange = {
            date: revisedDueDate
                ? revisedDueDate.substr(revisedDueDate.length - 1) === 'Z'
                    ? revisedDueDate
                    : revisedDueDate + 'Z'
                : dueDateRange ? dueDateRange.date : undefined
        };
        return revisedDueRange;
    }

    // Check if user has at least an allowed role
    private userInRole(roles: string[]): boolean {
        //this._currentUser.roles
        return _.some(this._authenticationService.authentication.roles, r => roles.indexOf(r) >= 0);
    }

    // Update due date and revised due date picker options
    private updateDueDatesOptions() {
        if (this.dueDateOptions) {
            this.dueDateOptions.startDate = moment(this.dueRange.date);
        }
        if (this.revisedDueDateOptions) {
            this.revisedDueDateOptions.startDate = moment(this.revisedDueRange.date);
        }
    }
}

export const wtTicketModal: ComponentDefinition = {
    componentId: 'wtTicketModal',
    controller: TicketModalController,
    template: require('./ticketModal.component.html'),
    controllerAs: 'vm',
    bindings: {
        editedTicketViewModel: '<?',
        modalId: '@',
        parent: '<?',
        onSaved: '&?'
    }
};